/**
 * Sales-tax return worksheet
 * --------------------------
 * For one sales-tax registration (acctg_salestax_registration) and one filing period:
 *   - the periods the registration files for (monthly / quarterly / annual — quarters and
 *     the year follow the fiscal year end, 12-31 when none), clipped to the dates it is
 *     registered;
 *   - the tax CHARGED and the tax PAID in the period, from the filed documents of its
 *     books, by document type:
 *       sales invoice                                 → + charged
 *       credit memo issued / refund issued            → − charged
 *       expense receipt / purchase invoice / bill     → + paid
 *       credit memo received / refund received        → − paid
 *       anything else (payments, statements, quotes…) → not counted, listed so the user sees it
 *   - the same two figures from the books, as the movement of the registration's GL
 *     accounts (tax charged = credits − debits; tax paid = debits − credits).
 * Documents are matched to the registration by owner (the document's main owner), tax
 * type and jurisdiction (the rate's province; country-wide for a federal registration).
 * A document with no tax amount but a rate and a total gets the tax worked out of the
 * total (tax-included) and is marked `estimated`.
 *
 * Pure + unit-tested (salesTaxReturn.test.ts). Country-neutral: nothing here names one
 * country's form; the screen adds a country's wording where it has one.
 */
type Row = Record<string, any>;

export type Frequency = 'monthly' | 'quarterly' | 'annual';

export interface TaxPeriod {
  start: string;   // YYYY-MM-DD
  end: string;     // YYYY-MM-DD
  label: string;
  /** The period has not ended yet. */
  open: boolean;
}

// ---- plain-date helpers (no time zones: Y/M/D integers) ----
const pad = (n: number) => String(n).padStart(2, '0');
const fmt = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const iso = (v: any) => String(v == null ? '' : v).slice(0, 10);

function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return fmt(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** The date in (year, month) on the fiscal year end's day, clamped to the month's length. */
function onDay(y: number, m: number, day: number): string {
  while (m < 1) { m += 12; y -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  return fmt(y, m, Math.min(day, daysIn(y, m)));
}

function parseFye(fye: string | null | undefined): { m: number; d: number } {
  const hit = /^(\d{2})-(\d{2})$/.exec(String(fye || ''));
  if (!hit) return { m: 12, d: 31 };
  const m = Math.min(12, Math.max(1, Number(hit[1])));
  return { m, d: Math.min(31, Math.max(1, Number(hit[2]))) };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** End dates of the periods, oldest first, from the period containing `from` to the one containing `to`. */
function periodEnds(freq: Frequency, fye: string | null | undefined, from: string, to: string): string[] {
  const { m, d } = parseFye(fye);
  const step = freq === 'monthly' ? 1 : freq === 'quarterly' ? 3 : 12;
  // Monthly periods are calendar months (ends on the month's last day).
  const dayOf = freq === 'monthly' ? 31 : d;
  const startYear = Number(from.slice(0, 4)) - 1;
  const ends: string[] = [];
  // Walk ends from a year before `from` (anchored on the fiscal year end month) forward.
  for (let k = 0; k < 12 * 60; k += step) {
    const end = onDay(startYear, (freq === 'monthly' ? 1 : m) + k, dayOf);
    if (end < from) continue;
    ends.push(end);
    if (end >= to) break;
  }
  return ends;
}

export function buildPeriods(opts: {
  frequency: Frequency | string;
  fiscalYearEnd?: string | null;
  registeredFrom?: string | null;
  registeredTo?: string | null;
  today: string;
  /** How far back when the registration has no start date (default 3 years). */
  yearsBack?: number;
}): TaxPeriod[] {
  const freq: Frequency = opts.frequency === 'monthly' || opts.frequency === 'annual' ? opts.frequency : 'quarterly';
  const regFrom = iso(opts.registeredFrom);
  const regTo = iso(opts.registeredTo);
  const today = iso(opts.today);
  const back = `${Number(today.slice(0, 4)) - (opts.yearsBack ?? 3)}${today.slice(4)}`;
  const from = regFrom || back;
  const to = regTo && regTo < today ? regTo : today;
  if (from > to) return [];

  const ends = periodEnds(freq, opts.fiscalYearEnd, from, to);
  const step = freq === 'monthly' ? 1 : freq === 'quarterly' ? 3 : 12;
  const out: TaxPeriod[] = [];
  for (const end of ends) {
    // The period starts the day after the previous period's end.
    const [ey, em] = end.split('-').map(Number);
    const { d } = parseFye(opts.fiscalYearEnd);
    const prevEnd = onDay(ey, em - step, freq === 'monthly' ? 31 : d);
    let start = addDays(prevEnd, 1);
    let stop = end;
    if (regFrom && start < regFrom) start = regFrom;
    if (regTo && stop > regTo) stop = regTo;
    if (start > stop) continue;
    const label = freq === 'monthly' && start.slice(8) === '01' && stop === end
      ? `${MONTHS[em - 1]} ${ey}`
      : freq === 'annual' ? `Year ${start} to ${stop}` : `${start} to ${stop}`;
    out.push({ start, end: stop, label, open: end >= today });
  }
  return out.reverse();
}

// ---- document side ----
export const CHARGED_CODES: Record<string, 1 | -1> = { SINV: 1, CMSales: -1, RFNDIssued: -1 };
export const PAID_CODES: Record<string, 1 | -1> = { EXPS: 1, PINV: 1, STMT: 1, CRMM: -1, RFNDRcvd: -1 };
/** Filing statuses whose documents count (Review = not filed yet). */
const COUNTED_STATUSES = new Set(['filed', 'processed']);

export interface ReturnLine {
  document_id: number;
  date: string;
  party: string;
  docType: string;
  total: number | null;
  tax: number;          // signed: a credit memo / refund is negative
  estimated: boolean;
}

export interface ReturnResult {
  charged: number;
  paid: number;
  /** charged − paid: positive = owing, negative = refund. */
  net: number;
  chargedLines: ReturnLine[];
  paidLines: ReturnLine[];
  /** Tax on documents of a type that is not counted (payments, statements …). */
  notCounted: ReturnLine[];
  /** Documents in the period with this tax that are not filed yet. */
  pendingCount: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** The tax on a document: its tax amount, else worked out of a tax-included total. */
export function documentTax(doc: Row, ratePercent: number | null): { tax: number; estimated: boolean } | null {
  const amt = doc.tax_amount;
  if (amt != null && amt !== '' && !Number.isNaN(Number(amt))) return { tax: Math.abs(Number(amt)), estimated: false };
  const total = Number(doc.total_amount);
  const rate = ratePercent ?? (doc.tax_rate != null && doc.tax_rate !== '' ? Number(doc.tax_rate) : null);
  if (!rate || !total) return null;
  return { tax: round2(Math.abs(total) * rate / (100 + rate)), estimated: true };
}

export function returnFromDocuments(input: {
  documents: Row[];
  docuTypes: Row[];
  rates: Row[];
  registration: Row;
  period: { start: string; end: string };
}): ReturnResult {
  const { documents, docuTypes, rates, registration: reg, period } = input;
  const codeOf = new Map<number, string>(docuTypes.map(t => [Number(t.docu_type_id), String(t.docu_type_code || '')]));
  const nameOf = new Map<number, string>(docuTypes.map(t => [Number(t.docu_type_id), String(t.docu_type_name || '')]));
  const rateById = new Map<number, Row>(rates.map(r => [Number(r.tax_rate_id), r]));
  const regState = reg.state_id == null || reg.state_id === '' ? null : Number(reg.state_id);

  const res: ReturnResult = { charged: 0, paid: 0, net: 0, chargedLines: [], paidLines: [], notCounted: [], pendingCount: 0 };
  for (const doc of documents) {
    if (Number(doc.is_deleted) === 1 || String(doc.status || '').toLowerCase() === 'deleted') continue;
    if (doc.owner_type !== reg.owner_type || Number(doc.owner_id) !== Number(reg.owner_id)) continue;
    if (Number(doc.tax_type_id) !== Number(reg.tax_type_id)) continue;
    const date = iso(doc.date_of_document);
    if (!date || date < period.start || date > period.end) continue;
    const rate = doc.tax_rate_id != null && doc.tax_rate_id !== '' ? rateById.get(Number(doc.tax_rate_id)) : undefined;
    const docState = rate && rate.state_id != null && rate.state_id !== '' ? Number(rate.state_id) : null;
    if (rate && docState !== regState) continue;   // another province's tax of the same type

    if (!COUNTED_STATUSES.has(String(doc.status || '').toLowerCase())) { res.pendingCount++; continue; }
    const t = documentTax(doc, rate ? Number(rate.rate_percent) : null);
    if (!t) continue;
    const code = codeOf.get(Number(doc.docu_type_id)) || '';
    const line = (sign: number): ReturnLine => ({
      document_id: Number(doc.document_id),
      date,
      party: String(doc.party_name || ''),
      docType: nameOf.get(Number(doc.docu_type_id)) || 'No type',
      total: doc.total_amount != null && doc.total_amount !== '' ? Number(doc.total_amount) : null,
      tax: round2(sign * t.tax),
      estimated: t.estimated,
    });
    if (CHARGED_CODES[code]) { const l = line(CHARGED_CODES[code]); res.chargedLines.push(l); res.charged += l.tax; }
    else if (PAID_CODES[code]) { const l = line(PAID_CODES[code]); res.paidLines.push(l); res.paid += l.tax; }
    else res.notCounted.push(line(1));
  }
  const byDate = (a: ReturnLine, b: ReturnLine) => a.date.localeCompare(b.date) || a.document_id - b.document_id;
  res.chargedLines.sort(byDate); res.paidLines.sort(byDate); res.notCounted.sort(byDate);
  res.charged = round2(res.charged);
  res.paid = round2(res.paid);
  res.net = round2(res.charged - res.paid);
  return res;
}

// ---- GL side ----
/**
 * Movement of one GL account in the period, from posted journals:
 * side 'credit' = credits − debits (a liability such as tax charged),
 * side 'debit'  = debits − credits (tax paid that can be claimed back).
 * Returns null when no account is set.
 */
export function glMovement(input: {
  coaId: number | string | null | undefined;
  journals: Row[];
  lines: Row[];
  period: { start: string; end: string };
  side: 'credit' | 'debit';
}): number | null {
  const { coaId, journals, lines, period, side } = input;
  if (coaId == null || coaId === '') return null;
  const inPeriod = new Set<number>(journals
    .filter(j => Number(j.is_deleted) !== 1 && String(j.status || 'posted').toLowerCase() === 'posted' &&
      iso(j.journal_date) >= period.start && iso(j.journal_date) <= period.end)
    .map(j => Number(j.journal_id)));
  let dr = 0, cr = 0;
  for (const l of lines) {
    if (Number(l.is_deleted) === 1 || Number(l.coa_id) !== Number(coaId) || !inPeriod.has(Number(l.journal_id))) continue;
    dr += Number(l.debit) || 0;
    cr += Number(l.credit) || 0;
  }
  return round2(side === 'credit' ? cr - dr : dr - cr);
}

/** CSV of the worksheet (one row per document, then the totals). */
export function returnCsv(r: ReturnResult, heading: string): string {
  const q = (v: any) => {
    const s = String(v == null ? '' : v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows: (string | number)[][] = [[heading], [], ['Section', 'Date', 'Party', 'Document type', 'Total', 'Tax', 'Estimated']];
  const add = (section: string, ls: ReturnLine[]) => ls.forEach(l =>
    rows.push([section, l.date, l.party, l.docType, l.total ?? '', l.tax.toFixed(2), l.estimated ? 'yes' : '']));
  add('Tax charged', r.chargedLines);
  add('Tax paid', r.paidLines);
  rows.push([], ['Total tax charged', '', '', '', '', r.charged.toFixed(2)]);
  rows.push(['Total tax paid', '', '', '', '', r.paid.toFixed(2)]);
  rows.push([r.net >= 0 ? 'Net owing' : 'Net refund', '', '', '', '', Math.abs(r.net).toFixed(2)]);
  return rows.map(row => row.map(q).join(',')).join('\n');
}
